import {
    Icon,
    IDropdownOption,
    IDropdownProps,
    MessageBarType,
    PrimaryButton,
    Spinner,
    SpinnerSize,
    Stack,
    TooltipHost,
} from '@fluentui/react';
import React, { FC, FormEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    ApiException,
    Label,
    Section,
    SectionSize,
    TextField,
    VirtualizedComboBox,
    MessageBar,
    Dropdown,
} from '@eta/components';
import './TaxpayerInformation.scss';
import { WizardStateAndLogic } from '../common/component/WizardPivot';
import {
    LanguageTypes,
    NewSessionResponse,
    SessionContactdetailsRequestDto,
} from './Service/TaxpayerProxy';
import { ServiceCreator } from './Service/client';
import { itemsKey } from './TaxpayerSelfRegistryMain';

export enum DocumentSubmitType{
    Invoice = 'B2B',
    Reciept = 'B2C'
}
export interface TaxpayerInformationProps {
    setFooter: (footer: JSX.Element) => void;
    taxpayerSelfRegistryWizard: WizardStateAndLogic;
    rin?: string;
    newSession?: NewSessionResponse;
    setDefaultLanguage: (type: LanguageTypes) => void;
    setCurrentMobileNo: (value?: string) => void;
}

export const TaxpayerInformation: FC<TaxpayerInformationProps> = (
    props: TaxpayerInformationProps
) => {
    const { t } = useTranslation(['SignUp', 'resources']);
    const {
        setFooter,
        taxpayerSelfRegistryWizard,
        rin,
        newSession,
        setDefaultLanguage,
        setCurrentMobileNo,
    } = props;
    const [notificationEmail, setNotificationEmail] = useState<string>();
    const [mobileNo, setMobileNo] = useState<string>();
    const [currentProfileLanguage, setCurrentProfileLanguage] =
        useState<string>(LanguageTypes.En);
    const [canRegister, setCanRegister] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [errorrMsg, setErrorrMsg] = useState<string>();
    const client = ServiceCreator.getTaxpayerManagementPublicClient(true);

    const DefaultLanguage = [
        { key: LanguageTypes.En, text: t('resources:constants.English') },
        { key: LanguageTypes.Ar, text: t('resources:constants.Arabic') },
    ];

    const Tags: IDropdownOption[] = [
        { key: DocumentSubmitType.Invoice, text: t('resources:Doc_Invoices') },
        { key: DocumentSubmitType.Reciept, text: t('resources:Doc_Reciepts') }
    ];
    const isEditableTag = true;
    const [currentTags, setCurrentTags] = useState<string[]>([]);
    const [currentTagsErrorMsg, setCurrentTagsErrorMsg] = useState<string>();

    const onRenderTagsLabel = (
          ddProps: IDropdownProps | undefined,
          df?: (renderProps: IDropdownProps | undefined) => JSX.Element | null
      ): JSX.Element | null=> {
          if (ddProps) {
              return (
                  <Stack horizontal verticalAlign="start" className="TaxpayerTagsLabel">
                      <span>{ddProps.label}&nbsp;&nbsp;</span>
                      <TooltipHost
                          className='TaxpayerTagsTooltip'
                          content={(
                              <div>
                                  <div>
                                      <span className="bold">
                                          {t('resources:Invoices')}&nbsp;&nbsp;
                                      </span>
                                      <span>
                                          {t('resources:B2B_Transactions')}&nbsp;&nbsp;
                                      </span>
                                  </div>
                                  <br/>
                                  <br/>
                                  <div>
                                      <span className="bold">
                                          {t('resources:Receipts')}&nbsp;&nbsp;
                                      </span>
                                      <span>
                                          {t('resources:B2C_Transactions')}&nbsp;&nbsp;
                                      </span>
                                  </div>
                              </div>
                            )}
                      >
                          <Icon iconName="Info" />
                      </TooltipHost>
                  </Stack>
              );
          }
          if (df) {
              return df(ddProps);
          }
          return null;
    };

    const onChangeTagsDropDown = (
        _: FormEvent<HTMLDivElement>,
        option: IDropdownOption | undefined,
    ): void => {
        if (option) {
            const newTags: string[] = option.selected
                ? [...currentTags, option.key as string]
                : currentTags.filter((key: string) => key !== option.key);
            setCurrentTags(newTags);
            setCurrentTagsErrorMsg(newTags.length ? '' : t('resources:Choose_One_Option'));
        }
    };

    const registerContactdetails = async () => {
        try {
            setIsLoading(true);
            setErrorrMsg(undefined);
            const response = await client.sessionContactdetails(
                newSession?.sessionId ?? '',
                new SessionContactdetailsRequestDto({
                    email: notificationEmail,
                    phone: mobileNo,
                    preferredLanguage: currentProfileLanguage as LanguageTypes,
                    submissionTag: currentTags?.filter(element => element).join(',')
                })
            );
            if (response.isSuccess) {
                setDefaultLanguage(currentProfileLanguage as LanguageTypes);
                setCurrentMobileNo(mobileNo);
                taxpayerSelfRegistryWizard.ChangeTabState(
                    itemsKey.Information,
                    'completed'
                );
                taxpayerSelfRegistryWizard.nextTab();
            } else setErrorrMsg(t('PleaseCheckErrors'));
        } catch (ex) {
            taxpayerSelfRegistryWizard.ChangeTabState(
                itemsKey.Information,
                'error'
            );
            const exception = ex as ApiException;
            setErrorrMsg(
                exception?.serviceError
                    ? exception?.serviceError.toString()
                    : exception.toString()
            );
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        setFooter(
            <PrimaryButton
                className="continue"
                menuIconProps={{ iconName: 'Forward' }}
                disabled={!canRegister || isLoading}
                text={t('Continue')}
                onClick={() => registerContactdetails()}
            >
                {isLoading && <Spinner size={SpinnerSize.small} />}
            </PrimaryButton>
        );
    }, [
        canRegister,
        t,
        isLoading,
        currentProfileLanguage,
        notificationEmail,
        mobileNo,
    ]);

    useEffect(() => {
        setCanRegister(!!notificationEmail && !!mobileNo && !!currentTags.length);
    }, [notificationEmail, mobileNo, currentTags.length]);

    return (
        <div className="SignUp_TaxpayerInformation">
            {errorrMsg && (
                <MessageBar
                    messageBarType={MessageBarType.error}
                    isMultiline={false}
                    className="messageBar"
                    onDismiss={() => {
                        setErrorrMsg(undefined);
                    }}
                >
                    {errorrMsg}
                </MessageBar>
            )}
            <div className="TaxpayerInformationHeader">
                <p>{`2. ${t('TaxpayerInformation')} | ${rin ?? ''}`}</p>
            </div>
            <div className="SignUp_TaxpayerInformationContent">
                <Label className="label-fillInformation">
                    {t('PleasefillInformation')}
                </Label>
                <div className="companyFields">
                    <TextField
                        label={t('CompanyName')}
                        disabled
                        readOnly
                        value={newSession?.taxpayerInfo?.namePrimaryLang ?? ''}
                    />
                    <TextField
                        label={t('CompanyName_AR')}
                        disabled
                        readOnly
                        value={
                            newSession?.taxpayerInfo?.nameSecondaryLang ?? ''
                        }
                    />
                </div>
                <Section
                    size={SectionSize.h3}
                    title={t('TaxpayerPreferences')}
                />
                <div className="information">
                    <div className="innerGrid">
                        <VirtualizedComboBox
                            selectedKey={currentProfileLanguage}
                            onChange={(_, option) =>
                                setCurrentProfileLanguage(
                                    option?.key
                                        ? (option.key as LanguageTypes)
                                        : LanguageTypes.None
                                )
                            }
                            options={DefaultLanguage}
                            label={t('DefaultLanguage')}
                            required
                        />
                        <TextField
                            label={t('NotificationE-Mail')}
                            required
                            value={notificationEmail}
                            onChange={(_, v) => setNotificationEmail(v)}
                        />
                    </div>
                    <div className="innerGrid">
                        <TextField
                            label={t('MobileNumber')}
                            required
                            value={mobileNo}
                            onChange={(_, v) => setMobileNo(v)}
                        />
                        <Dropdown
                            selectedKeys={currentTags}
                            options={Tags}
                            label={t('resources:Documents_to_submit')}
                            multiSelect
                            onChange={onChangeTagsDropDown}
                            readOnly={!isEditableTag}
                            required={isEditableTag}
                            onRenderLabel={onRenderTagsLabel}
                            errorMessage={currentTagsErrorMsg}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};
